import type { Keyboard, KeyGeometry, QmkBoardLayer } from "@/domain/types";
import { assignSplitRegions, normalizeOrigin } from "@/hardware/geometry";
import { type KLEData, parseKLEDetailed } from "./kle-import";
import { isBlank, isTransparent, keycodeInfo, keycodeLegend } from "./qmk-keycodes";

/** The VIA definition file (via.json): geometry plus the row,col to physical-position map. */
export interface ViaDefinition {
  name?: string;
  matrix: { rows: number; cols: number };
  layouts: { keymap: KLEData };
}

/** The saved VIA keymap file (*.layout.json): per-layer keycode arrays in matrix-slot order. */
export interface ViaKeymap {
  name?: string;
  vendorProductId?: number;
  layers: string[][];
}

/** What a VIA or QMK pair imports to (the contract, minus the dispatcher's format tag). */
export interface BoardImport {
  keyboard: Keyboard;
  // VIA/QMK import always produces keycoded layers (the KLE path is the only kle source)
  boardLayers: QmkBoardLayer[];
  defaultLayerId: string;
}

/**
 * The effective keycode per key after KC_TRNS fall-through: a transparent key takes the
 * base layer's keycode at the same position. The base is a parameter so live multi-layer stacking
 * can reuse this later; today callers pass the default layer. Shared by the chars and legends maps so
 * the transparency rule lives in one place.
 */
function resolveEffective(keycodes: string[], baseKeycodes: string[]): string[] {
  return keycodes.map((kc, i) => (isTransparent(kc) ? (baseKeycodes[i] ?? "KC_NO") : kc));
}

/** A layer's keycodes resolved to chars, selected over default (printable only; null = blank cap). */
export function resolveBoardLayerChars(
  keycodes: string[],
  baseKeycodes: string[],
): (string | null)[] {
  return resolveEffective(keycodes, baseKeycodes).map((kc) => keycodeInfo(kc).char);
}

/**
 * The display legend per key for a board layer: the active keycode's identity - a char,
 * else a fixed label ("Ctrl", "F5", "MO3") - so a gaming layer's thumb Ctrl reads "Ctrl" and never
 * the typing key's "Space". "" only for a genuinely empty cell (KC_NO). KC_TRNS falls through to
 * the base layer first, like chars. Kept separate from chars, which feeds parsing (printable only).
 */
export function resolveBoardLayerLegends(keycodes: string[], baseKeycodes: string[]): string[] {
  return resolveEffective(keycodes, baseKeycodes).map((kc) => keycodeLegend(kc));
}

/** Layer 0 unless DF() usage declares exactly one other in-range default (spec). */
export function defaultLayerIndex(layers: string[][]): number {
  const targets = new Set<number>();
  for (const layer of layers) {
    for (const kc of layer) {
      const m = /^DF\((\d+)\)$/.exec(String(kc).trim());
      if (m) targets.add(Number(m[1]));
    }
  }
  if (targets.size === 1) {
    const t = [...targets][0] as number;
    if (t >= 0 && t < layers.length) return t;
  }
  return 0;
}

/** keycodesPerLayer[L][i] = keycode of physical key i on layer L (already key-aligned). */
function buildBoardLayers(
  keyboardId: string,
  keycodesPerLayer: string[][],
  defaultIdx: number,
): { boardLayers: QmkBoardLayer[]; defaultLayerId: string } {
  const base = keycodesPerLayer[defaultIdx] ?? [];
  const boardLayers = keycodesPerLayer.map((keycodes, n) => ({
    kind: "qmk" as const,
    id: `${keyboardId}-layer-${n}`,
    name: `Layer ${n}`,
    keycodes,
    // the ACTIVE code per key, resolved once: KC_TRNS falls through to the base code exactly like
    // chars and legends do (a transparent key emits the base action, so it can receive a binding);
    // null only where the effective keycode genuinely emits nothing (KC_NO, MO(n), media keys)
    codes: resolveEffective(keycodes, base).map((kc) => keycodeInfo(kc).code),
    chars: resolveBoardLayerChars(keycodes, base),
    legends: resolveBoardLayerLegends(keycodes, base),
    empty: keycodes.every((kc) => isTransparent(kc) || isBlank(kc)),
  }));
  return { boardLayers, defaultLayerId: `${keyboardId}-layer-${defaultIdx}` };
}

/** Key identity comes from the default layer: KC_Q makes its key "KeyQ" so Profile Bindings attach. */
function assignCodes(keys: KeyGeometry[], defaultKeycodes: string[]): void {
  keys.forEach((k, i) => {
    k.code = keycodeInfo(defaultKeycodes[i] ?? "KC_NO").code ?? `x${i}`;
  });
}

/** VIA import: definition (geometry) x keymap (layers), aligned by index = row * cols + col. */
export function importVIA(
  definition: ViaDefinition,
  keymap: ViaKeymap,
  meta: { id: string; name?: string | null },
): BoardImport {
  const { rows, cols } = definition.matrix;
  const { keys, legends } = parseKLEDetailed(definition.layouts.keymap);
  if (!keys.length) throw new Error("No keys found in the VIA definition.");
  if (!keymap.layers.length) throw new Error("The keymap has no layers.");
  const slots = rows * cols;
  for (const layer of keymap.layers) {
    if (layer.length < slots) {
      throw new Error(
        `A keymap layer has ${layer.length} entries; the ${rows}x${cols} matrix needs ${slots}.`,
      );
    }
  }
  // each definition key's "legend" is its matrix address "row,col": a position label, not a character
  const claimed = new Set<number>();
  const matrixIndex = keys.map((_, i) => {
    const m = /^(\d+),(\d+)/.exec((legends[i] ?? "").trim());
    if (!m) throw new Error(`Key ${i + 1} in the definition has no row,col matrix position.`);
    const r = Number(m[1]);
    const c = Number(m[2]);
    if (r >= rows || c >= cols) {
      throw new Error(`Key ${i + 1} sits at ${r},${c}, outside the ${rows}x${cols} matrix.`);
    }
    const idx = r * cols + c;
    if (claimed.has(idx)) {
      throw new Error(
        `Two keys claim matrix position ${r},${c}. This definition likely uses layout options, which aren't supported yet.`,
      );
    }
    claimed.add(idx);
    return idx;
  });
  const keycodesPerLayer = keymap.layers.map((layer) =>
    matrixIndex.map((idx) => String(layer[idx] ?? "KC_NO")),
  );
  const defaultIdx = defaultLayerIndex(keymap.layers);
  assignCodes(keys, keycodesPerLayer[defaultIdx] ?? []);
  const size = normalizeOrigin(keys);
  assignSplitRegions(keys, size.width);
  const name = definition.name ?? keymap.name ?? meta.name ?? "Imported keyboard";
  const keyboard: Keyboard = { id: meta.id, name, width: size.width, height: size.height, keys };
  return { keyboard, ...buildBoardLayers(meta.id, keycodesPerLayer, defaultIdx) };
}

/** One key entry in a QMK info.json layout: geometry in the LAYOUT macro's argument order. */
interface QmkLayoutKey {
  x: number;
  y: number;
  w?: number;
  h?: number;
  r?: number;
  rx?: number;
  ry?: number;
  matrix?: [number, number];
  label?: string;
}

/** The board's info.json: geometry per LAYOUT macro. */
export interface QmkInfo {
  keyboard_name?: string;
  layouts: Record<string, { layout: QmkLayoutKey[] }>;
}

/** A QMK Configurator keymap.json: layers in LAYOUT macro argument order. */
export interface QmkKeymap {
  keyboard?: string;
  layout?: string;
  layers: string[][];
}

/** QMK import: keymap.json x info.json, aligned by LAYOUT macro array order (no matrix math). */
export function importQMK(
  info: QmkInfo,
  keymap: QmkKeymap,
  meta: { id: string; name?: string | null },
): BoardImport {
  const layouts = info.layouts ?? {};
  const names = Object.keys(layouts);
  const layoutName =
    keymap.layout && layouts[keymap.layout] ? keymap.layout : names.length === 1 ? names[0] : null;
  if (!layoutName || !layouts[layoutName]) {
    throw new Error(`The info.json has no layout named "${keymap.layout ?? "?"}".`);
  }
  const entries = layouts[layoutName].layout;
  if (!keymap.layers.length) throw new Error("The keymap has no layers.");
  const layers = keymap.layers.map((layer) => layer.map((kc) => String(kc)));
  for (const layer of layers) {
    if (layer.length !== entries.length) {
      throw new Error(
        `The keymap has ${layer.length} keycodes per layer; ${layoutName} has ${entries.length} keys.`,
      );
    }
  }
  if (!entries.length) throw new Error("No keys found in the info.json layout.");
  const keys: KeyGeometry[] = entries.map((e, i) => {
    const k: KeyGeometry = { code: `x${i}`, x: e.x, y: e.y, w: e.w ?? 1, h: e.h ?? 1 };
    if (e.r) {
      k.r = e.r;
      k.rx = e.rx ?? 0;
      k.ry = e.ry ?? 0;
    }
    return k;
  });
  const defaultIdx = defaultLayerIndex(layers);
  assignCodes(keys, layers[defaultIdx] ?? []);
  const size = normalizeOrigin(keys);
  assignSplitRegions(keys, size.width);
  const name = info.keyboard_name ?? keymap.keyboard ?? meta.name ?? "Imported keyboard";
  const keyboard: Keyboard = { id: meta.id, name, width: size.width, height: size.height, keys };
  return { keyboard, ...buildBoardLayers(meta.id, layers, defaultIdx) };
}
